import Cookies from 'js-cookie';

/**
 * Multi-Persona Token Management
 *
 * This service manages JWT tokens for different personas (user, prospect, admin).
 *
 * IMPORTANT: Token sources
 * 1. Frontend-managed tokens (jwtTokenUser, jwtTokenProspect, jwtTokenAdmin)
 *    - Stored in js-cookie and localStorage
 *    - Used for client-side auth state and Authorization header
 *
 * 2. Legacy server httpOnly cookies (AuthorizationUser, AuthorizationProspect, AuthorizationAdmin)
 *    - Deprecated and no longer required by the frontend
 *    - Server may still accept them if they exist
 *
 * The backend historically set a legacy 'Authorization' cookie during the transition period.
 * This service uses 'jwtToken' as the legacy key for backward compatibility.
 */

type Persona = 'user' | 'prospect' | 'admin';

export const TOKEN_KEYS: Record<Persona | 'legacy', string> = {
  legacy: 'jwtToken', // legacy key kept for backward compatibility
  user: 'jwtTokenUser',
  prospect: 'jwtTokenProspect',
  admin: 'jwtTokenAdmin',
};

const _getFromStorage = (key: string): string | undefined => {
  try {
    return Cookies.get(key) || localStorage.getItem(key) || undefined;
  } catch {
    return undefined;
  }
};

const COOKIE_OPTIONS = { path: '/' };

const _setToStorage = (key: string, value: string) => {
  try {
    Cookies.set(key, value, COOKIE_OPTIONS);
    localStorage.setItem(key, value);
  } catch {
    // ignore storage errors (e.g., SSR / private mode)
  }
};

const _removeFromStorage = (key: string) => {
  try {
    Cookies.remove(key, COOKIE_OPTIONS);
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

/**
 * Read a JWT for a specific persona.
 * - Uses persona-specific key first.
 * - Falls back to legacy key only when no other persona token is present (to avoid cross-persona leakage).
 */
export const readJwtToken = (
  persona?: Persona,
  allowLegacyFallback = true
): string | undefined => {
  const userToken = _getFromStorage(TOKEN_KEYS.user);
  const prospectToken = _getFromStorage(TOKEN_KEYS.prospect);
  const adminToken = _getFromStorage(TOKEN_KEYS.admin);
  const legacyToken = allowLegacyFallback
    ? _getFromStorage(TOKEN_KEYS.legacy)
    : undefined;

  if (persona === 'user') {
    if (userToken) return userToken;
    if (allowLegacyFallback && !prospectToken && !adminToken)
      return legacyToken;
    return undefined;
  }

  if (persona === 'prospect') {
    if (prospectToken) return prospectToken;
    if (allowLegacyFallback && !userToken && !adminToken) return legacyToken;
    return undefined;
  }

  if (persona === 'admin') {
    if (adminToken) return adminToken;
    if (allowLegacyFallback && !userToken && !prospectToken) return legacyToken;
    return undefined;
  }

  // Any persona: prefer scoped tokens, then legacy
  return userToken || prospectToken || adminToken || legacyToken;
};

/**
 * Persist a JWT for a given persona. Optionally keeps the legacy key for active sessions.
 */
export const persistJwtToken = (
  token: string,
  persona: Persona,
  keepLegacy = true
) => {
  const personaKey = TOKEN_KEYS[persona];
  _setToStorage(personaKey, token);

  if (keepLegacy) {
    _setToStorage(TOKEN_KEYS.legacy, token);
  }

  // Clear other persona-specific tokens to reduce accidental cross-usage
  (['user', 'prospect', 'admin'] as Persona[])
    .filter((p) => p !== persona)
    .forEach((p) => _removeFromStorage(TOKEN_KEYS[p]));
};

export const clearJwtTokens = (personas?: Array<Persona | 'legacy'>) => {
  const keys =
    personas && personas.length
      ? personas
      : (Object.keys(TOKEN_KEYS) as Array<Persona | 'legacy'>);
  keys.forEach((p) => _removeFromStorage(TOKEN_KEYS[p]));
};
